/**
 * The asset page's Versions tab (1.2.3): every version, newest first, with its number, date, who uploaded it, its
 * masters, its size and where its files are, its state (current, earlier, in the recycle bin with the purge date, or
 * purged) and what uses it. Actions, each with its reason when it isn't offered:
 *   Play            the version's proxy, read-only
 *   Make current    an earlier version (ready, not in the bin) becomes the current one again (Upload); its masters
 *                   come back with it, sequences keep the versions they pinned; not for graphics
 *   Delete version… an earlier version nothing uses, to the recycle bin (Delete old versions); never the current one
 *   Restore         one in the bin, until its purge has started
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, CornerUpLeft, Files, HardDrive, Play, RotateCcw, Trash2 } from 'lucide-react';
import { describeVersionUse, type AssetDetail, type VersionDTO } from '@prism/contracts';
import { errorMessage } from '../api/client';
import { useAssetVersions, useMe, useRecycleSettings, useVersionActions } from '../api/hooks';
import { Modal } from '../components/Modal';
import { toast } from '../components/Toasts';
import { bytes, dateTime, relative } from '../lib/format';

const STATE: Record<VersionDTO['state'], { label: string; cls: string }> = {
  current: { label: 'Current', cls: 'pill accent' }, earlier: { label: 'Earlier', cls: 'pill' },
  deleted: { label: 'In the recycle bin', cls: 'pill warning' }, purged: { label: 'Deleted permanently', cls: 'pill' },
};

/** Why a version can't be deleted, or null when it can. */
export function deleteBlocked(v: Pick<VersionDTO, 'state' | 'uses'>): string | null {
  if (v.state === 'current') return 'The current version can’t be deleted: upload a new version first, or delete the asset.';
  if (v.state !== 'earlier') return null;
  if (v.uses.length) return `In use: ${v.uses.map(describeVersionUse).join('; ')}`;
  return null;
}

/** Why an earlier version can't be made current, or null when it can. */
export function makeCurrentBlocked(v: Pick<VersionDTO, 'state' | 'status' | 'number'>, current: Pick<VersionDTO, 'status' | 'number'> | undefined): string | null {
  if (v.state !== 'earlier') return null;
  if (current && ['registered', 'processing', 'recording'].includes(current.status)) return `Version ${current.number} is still being processed; try again when it is ready.`;
  if (['registered', 'processing', 'recording'].includes(v.status)) return `Version ${v.number} is still being processed.`;
  if (v.status !== 'ready' && v.status !== 'partial') return `Version ${v.number} failed to process, so it can’t be made current.`;
  return null;
}

export function VersionsTab({ asset }: { asset: AssetDetail }) {
  const me = useMe().data!;
  const q = useAssetVersions(asset.id);
  const actions = useVersionActions(asset.id);
  const [deleting, setDeleting] = useState<VersionDTO | null>(null);
  const [playing, setPlaying] = useState<VersionDTO | null>(null);
  const [reverting, setReverting] = useState<VersionDTO | null>(null);
  const canDelete = me.permissions.includes('asset.delete_versions') && !asset.deleted;
  // Graphics keep PRISM Graphics' own versions; a placeholder has none to choose from.
  const canMakeCurrent = me.permissions.includes('asset.upload') && !asset.deleted && asset.mediaKind !== 'graphic' && !asset.graphic;
  const items = q.data?.items ?? [];
  const current = items.find((v) => v.state === 'current');

  const restore = async (v: VersionDTO) => {
    try {
      await actions.restore.mutateAsync(v.id);
      toast(`Version ${v.number} is back`);
    } catch (err) { toast(errorMessage(err), 'error'); }
  };

  return (
    <div data-testid="versions-tab">
      <p className="muted" style={{ marginTop: 0 }}>
        Every version of this asset, newest first. The current one is what the player, exports and the Library use; earlier versions keep their own masters and files
        until they are deleted. {canMakeCurrent ? 'Make current brings an earlier version back as the current one. ' : ''}{canDelete ? 'Deleting an earlier version moves it to the recycle bin first.' : ''}
      </p>
      {q.isLoading ? <div className="skeleton" style={{ height: 140 }} /> : q.isError ? <p className="faint">The versions could not be loaded: {errorMessage(q.error)}</p> : (
        <div className="versions-list" data-testid="versions-list">
          {items.map((v) => {
            const blocked = deleteBlocked(v);
            const st = STATE[v.state];
            return (
              <div className="version-row" key={v.id} data-testid="version-row" data-state={v.state} data-number={v.number}>
                <div className="version-main">
                  <div className="version-title">
                    <Files size={14} aria-hidden />
                    <strong>Version {v.number}</strong>
                    <span className={st.cls} data-testid="version-state">{st.label}</span>
                    <span className="faint" title={dateTime(v.createdAt)}>{relative(v.createdAt)}{v.createdBy ? ` by ${v.createdBy.displayName}` : ''}</span>
                    {v.madeCurrent && (
                      <span className="faint" data-testid="version-made-current" title={dateTime(v.madeCurrent.at)}>
                        · made current again{v.madeCurrent.by ? ` by ${v.madeCurrent.by}` : ''}, {relative(v.madeCurrent.at)}
                      </span>
                    )}
                  </div>
                  {v.originalFilename && <div className="version-sub break-any" title={v.originalFilename}>{v.originalFilename}</div>}
                  {v.state !== 'purged' && (
                    <div className="version-sub">
                      {v.masters.length > 0 && <span data-testid="version-masters">{v.masters.map((m) => m.primary ? `${m.variant} (primary)` : m.variant).join(' · ')}</span>}
                      <span data-testid="version-size">{bytes(v.sizeBytes)}{v.storedBytes > v.sizeBytes ? ` · ${bytes(v.storedBytes)} with every copy` : ''}</span>
                    </div>
                  )}
                  {v.locations.length > 0 && (
                    <div className="master-locations">
                      {v.locations.map((l) => (
                        <span key={l.id} className="master-location" title={l.managed ? 'Managed by PRISM: deleted when the version is purged' : 'Catalogued in place: never deleted by PRISM'}>
                          <HardDrive size={12} /> {l.name} · {bytes(l.bytes)}{l.managed ? '' : ' (in place)'}
                        </span>
                      ))}
                    </div>
                  )}
                  {v.state === 'deleted' && (
                    <div className="master-note" data-testid="version-purge">
                      <Trash2 size={12} aria-hidden /> Deleted {relative(v.deletedAt)}{v.deletedBy ? ` by ${v.deletedBy}` : ''}; purged {v.purgeAfter ? `on ${dateTime(v.purgeAfter)}` : 'soon'}.
                      {v.purgeError && <span style={{ color: 'var(--danger)' }}> {v.purgeError}</span>}
                    </div>
                  )}
                  {v.state === 'purged' && <div className="master-note faint">Deleted permanently {relative(v.purgedAt)}; its number is not used again.</div>}
                  {v.uses.length > 0 && (
                    <ul className="version-uses" data-testid="version-uses">
                      {v.uses.map((u, i) => (
                        <li key={i}><AlertTriangle size={12} aria-hidden /> {u.link ? <Link to={u.link}>{describeVersionUse(u)}</Link> : describeVersionUse(u)}</li>
                      ))}
                    </ul>
                  )}
                  {canDelete && v.state === 'current' && items.length > 1 && <div className="master-note faint" data-testid="version-current-note">{blocked}</div>}
                </div>
                <div className="master-actions">
                  {v.proxyUrl && v.state !== 'purged' && (
                    <button className="btn ghost sm" onClick={() => setPlaying(v)} data-testid="version-play" aria-label={`Play version ${v.number}`}><Play size={13} /> Play</button>
                  )}
                  {canMakeCurrent && v.state === 'earlier' && current && (() => {
                    const why = makeCurrentBlocked(v, current);
                    return (
                      <button className="btn ghost sm" disabled={Boolean(why) || actions.makeCurrent.isPending} title={why ?? `Make version ${v.number} the current version again`}
                        onClick={() => setReverting(v)} data-testid="version-make-current"><CornerUpLeft size={13} /> Make current</button>
                    );
                  })()}
                  {canDelete && v.state === 'earlier' && (
                    <button className="btn ghost sm danger" disabled={Boolean(blocked) || actions.remove.isPending} title={blocked ?? 'Move this version to the recycle bin'}
                      onClick={() => setDeleting(v)} data-testid="version-delete"><Trash2 size={13} /> Delete version…</button>
                  )}
                  {canDelete && v.state === 'deleted' && (
                    <button className="btn sm" disabled={actions.restore.isPending} onClick={() => void restore(v)} data-testid="version-restore"><RotateCcw size={13} /> Restore</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {deleting && <DeleteVersionDialog asset={asset} version={deleting} onClose={() => setDeleting(null)} onDelete={async () => {
        try {
          const r = await actions.remove.mutateAsync(deleting.id);
          toast(r.recycleBin ? `Version ${r.version} is in the recycle bin until ${dateTime(r.purgeAfter)}` : `Version ${r.version} is deleted; it can be restored for a few minutes`);
          setDeleting(null);
        } catch (err) { toast(errorMessage(err), 'error'); }
      }} />}
      {reverting && current && <MakeCurrentDialog asset={asset} version={reverting} current={current} busy={actions.makeCurrent.isPending} onClose={() => setReverting(null)} onConfirm={async () => {
        try {
          const r = await actions.makeCurrent.mutateAsync({ number: reverting.number, expectedCurrent: current.number });
          toast(`Version ${r.version} is the current version again; version ${r.previousVersion} is kept as an earlier version`);
          setReverting(null);
        } catch (err) { toast(errorMessage(err), 'error'); }
      }} />}
      {playing && (
        <Modal title={`Version ${playing.number} of ${asset.title}`} wide onClose={() => setPlaying(null)} actions={<button className="btn" onClick={() => setPlaying(null)}>Close</button>}>
          <video className="version-player" src={playing.proxyUrl!} poster={playing.posterUrl ?? undefined} controls autoPlay data-testid="version-player" />
          <p className="faint" style={{ margin: '6px 0 0', fontSize: 12 }}>The proxy of version {playing.number}, read-only. The current version is version {asset.versionNumber}.</p>
        </Modal>
      )}
    </div>
  );
}

function MakeCurrentDialog({ asset, version, current, busy, onClose, onConfirm }: { asset: AssetDetail; version: VersionDTO; current: VersionDTO; busy: boolean; onClose: () => void; onConfirm: () => Promise<void> }) {
  const variants = version.masters.filter((m) => !m.primary).map((m) => m.variant);
  return (
    <Modal title={`Make version ${version.number} current?`} onClose={onClose} actions={<>
      <button className="btn ghost" onClick={onClose}>Cancel</button>
      <button className="btn primary" disabled={busy} data-testid="version-make-current-confirm" onClick={() => void onConfirm()}><CornerUpLeft size={14} /> Make version {version.number} current</button>
    </>}>
      <p style={{ marginTop: 0 }} data-testid="version-make-current-text">
        Version {version.number} of “{asset.title}” becomes the current version again, instead of version {current.number}. Nothing is copied or deleted: version {current.number} stays
        as an earlier version, with its own masters, and can be made current again later.
      </p>
      <p className="muted" style={{ marginBottom: 0 }}>
        The masters of version {version.number} come back with it{variants.length ? ` (${variants.join(', ')})` : ''}; the player, the Library card, search and exports use version {version.number} from now on.
        Sequences keep the versions they pinned. The next upload is still a new number.
      </p>
    </Modal>
  );
}

function DeleteVersionDialog({ asset, version, onClose, onDelete }: { asset: AssetDetail; version: VersionDTO; onClose: () => void; onDelete: () => Promise<void> }) {
  const settings = useRecycleSettings().data;
  const [busy, setBusy] = useState(false);
  const inPlace = version.locations.filter((l) => !l.managed);
  return (
    <Modal title={`Delete version ${version.number}?`} onClose={onClose} actions={<>
      <button className="btn ghost" onClick={onClose}>Cancel</button>
      <button className="btn danger" disabled={busy} data-testid="version-delete-confirm" onClick={() => { setBusy(true); void onDelete().finally(() => setBusy(false)); }}><Trash2 size={14} /> Move to recycle bin</button>
    </>}>
      <p style={{ marginTop: 0 }}>
        Version {version.number} of “{asset.title}” ({bytes(version.sizeBytes)}{version.masters.length > 1 ? `, ${version.masters.length} masters` : ''}) goes to the recycle bin.{' '}
        {settings?.enabled === false
          ? `The recycle bin is off: it can be restored for ${Math.round((settings.undoSeconds ?? 120) / 60)} minutes, then it is deleted.`
          : `It can be restored there for ${settings?.retentionDays ?? 30} days; then its files are deleted.`}
      </p>
      <p className="muted" style={{ marginBottom: 0 }}>
        The asset, its metadata and the current version {asset.versionNumber} stay as they are. Files PRISM manages are deleted when it is purged
        {inPlace.length ? `; files catalogued in place (${inPlace.map((l) => l.name).join(', ')}) are never deleted, only taken out of the catalog` : ''}. Its number is not used again.
      </p>
    </Modal>
  );
}
